import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, ChevronRight, Copy, Loader, X } from "lucide-react";
import type { ChangeSetInfo, ChatMessage, CommandView, ToolActivity } from "@racler/protocol";
import { Markdown } from "./Markdown.tsx";
import { CommandCard } from "./CommandCard.tsx";
import { ChangeSetCard } from "./ChangeSetCard.tsx";
import { usageLabel } from "../lib/chatReducer.ts";

export interface CommandActions {
  onApprove: (c: CommandView) => Promise<void>;
  onDeny: (c: CommandView) => Promise<void>;
  onCancel: (c: CommandView) => Promise<void>;
}

const TOOL_LABEL: Record<string, string> = {
  listFiles: "Listing files",
  searchFiles: "Searching files",
  readFile: "Reading",
  runCommand: "Command",
  proposeCreateFile: "Proposing new file",
  proposeModifyFile: "Proposing change to",
  proposeDeleteFile: "Proposing deletion of",
  proposeRenameFile: "Proposing rename of",
};

function target(a: ToolActivity): string {
  const v = a.args["path"] ?? a.args["query"] ?? a.args["executable"] ?? a.args["from"];
  return typeof v === "string" && v !== "" ? v : a.name === "listFiles" ? "." : "";
}

function ToolLine({ a }: { a: ToolActivity }) {
  const state = a.ok === null ? "running" : a.ok ? "ok" : "failed";
  return (
    <li className={`tool tool-${state}`} data-testid={`tool-${a.name}`} data-state={state}>
      <span className="tool-dot" aria-hidden>
        {state === "running" ? (
          <Loader size={12} className="spin" />
        ) : state === "ok" ? (
          <Check size={12} />
        ) : (
          <X size={12} />
        )}
      </span>
      <span className="tool-name">{TOOL_LABEL[a.name] ?? a.name}</span> <code>{target(a)}</code>
      {a.summary && <span className="muted small"> — {a.summary}</span>}
    </li>
  );
}

const COPIED_MS = 1500;

/** Copies the raw (Markdown source) text of a response; shows a checkmark briefly on success. */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = () => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), COPIED_MS);
      },
      () => setCopied(false),
    );
  };
  const label = copied ? "Copied" : "Copy";
  return (
    <button
      type="button"
      className={`icon-btn msg-copy${copied ? " copied" : ""}`}
      title={label}
      aria-label={label}
      onClick={copy}
      data-testid="copy-response"
    >
      {copied ? (
        <Check size={13} strokeWidth={1.75} aria-hidden />
      ) : (
        <Copy size={13} strokeWidth={1.75} aria-hidden />
      )}
    </button>
  );
}

const COLLAPSE_AFTER = 4;

/** Tool activity stays compact: with many calls only the latest are shown until expanded. */
function ToolList({ activity }: { activity: ToolActivity[] }) {
  const [expanded, setExpanded] = useState(false);
  const running = activity.some((a) => a.ok === null);
  const long = activity.length > COLLAPSE_AFTER;
  const shown = long && !expanded ? activity.slice(-2) : activity;
  const failed = activity.filter((a) => a.ok === false).length;
  return (
    <div className="tools-wrap">
      {long && (
        <button className="tools-toggle" onClick={() => setExpanded((v) => !v)} data-testid="tools-toggle">
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {activity.length} tool calls{running ? " · running" : ""}
          {failed ? ` · ${failed} failed` : ""}
        </button>
      )}
      <ul className="tools" data-testid="tool-activity">
        {shown.map((a) => (
          <ToolLine key={a.toolCallId} a={a} />
        ))}
      </ul>
    </div>
  );
}

/**
 * Model output is untrusted: it is rendered as sanitized Markdown (see Markdown.tsx: no raw HTML, no images,
 * http(s)/mailto links only), so a malicious file or prompt injection cannot script the IDE origin.
 */
export function Message({
  m,
  onRetry,
  changeSets = [],
  onReview,
  commands = [],
  commandActions,
}: {
  m: ChatMessage;
  onRetry?: (() => void) | undefined;
  changeSets?: ChangeSetInfo[];
  onReview?: ((id: string) => void) | undefined;
  commands?: CommandView[];
  commandActions?: CommandActions | undefined;
}) {
  if (m.role === "user") {
    return (
      <div className="msg msg-user" data-testid="msg-user">
        <div className="msg-role">You</div>
        {m.contextItems && m.contextItems.length > 0 && (
          <div className="msg-context-chips" data-testid="msg-context-chips">
            {m.contextItems.map((item, idx) => (
              <span key={idx} className="msg-context-chip" data-testid={`user-chip-${item.type}`}>
                <span className="chip-badge">
                  [
                  {item.type === "project_file"
                    ? "file"
                    : item.type === "project_folder"
                      ? "folder"
                      : item.type === "editor_selection"
                        ? "selection"
                        : "attachment"}
                  ]
                </span>{" "}
                <span className="chip-label">
                  {item.type === "attachment" ? item.name : item.path}
                  {item.type === "editor_selection" ? `:${item.startLine}-${item.endLine}` : ""}
                </span>
              </span>
            ))}
          </div>
        )}
        <div className="msg-text">{m.content}</div>
      </div>
    );
  }
  const running = m.status === "running";
  return (
    <div
      className={`msg msg-assistant status-${m.status}`}
      data-testid="msg-assistant"
      data-status={m.status}
    >
      <div className="msg-role">Agent {m.model && <span className="muted small">· {m.model}</span>}</div>
      {m.activity.length > 0 && <ToolList activity={m.activity} />}
      {m.content !== "" && (
        <div className="msg-text" data-testid="answer">
          <Markdown text={m.content} />
        </div>
      )}
      {running && m.content === "" && m.activity.every((a) => a.ok !== null) && (
        <div className="muted small">Thinking…</div>
      )}
      {onReview && changeSets.map((cs) => <ChangeSetCard key={cs.id} cs={cs} onReview={onReview} />)}
      {commandActions && commands.map((c) => <CommandCard key={c.id} c={c} {...commandActions} />)}
      {m.stopReason === "awaiting_approval" && (
        <div className="muted small paused" data-testid="run-paused">
          Paused — waiting for your decision. Approve to continue. Reject stops this run.
        </div>
      )}
      {m.status === "cancelled" && (
        <div className="muted small">
          {changeSets.some((c) => c.status === "rejected") ? "Stopped — changes rejected" : "Stopped."}
        </div>
      )}
      {m.status === "error" && (
        <div className="msg-error" role="alert">
          {m.error ?? "The response failed."}{" "}
          {onRetry && (
            <button className="link-btn" onClick={onRetry}>
              Retry
            </button>
          )}
        </div>
      )}
      {(m.usage || m.content !== "") && (
        <div className="msg-footer">
          {m.usage && <span className="muted small usage">{usageLabel(m.usage)}</span>}
          {m.content !== "" && <CopyButton text={m.content} />}
        </div>
      )}
    </div>
  );
}

export function MessageList({
  messages,
  onRetry,
  changeSets = [],
  onReview,
  commands = [],
  commandActions,
}: {
  messages: ChatMessage[];
  onRetry: () => void;
  changeSets?: ChangeSetInfo[];
  onReview?: (id: string) => void;
  commands?: CommandView[];
  commandActions?: CommandActions;
}) {
  const end = useRef<HTMLDivElement>(null);
  const last = messages[messages.length - 1];
  const stick = useRef(true);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (stick.current) end.current?.scrollIntoView({ block: "end" });
  }, [messages.length, last?.content.length, last?.activity.length, changeSets.length, commands.length]);
  return (
    <div
      className="chat-scroll"
      ref={box}
      onScroll={() => {
        const el = box.current;
        if (el) stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
      }}
      data-testid="chat-messages"
    >
      {messages.length === 0 && (
        <div className="muted small chat-empty">
          Ask about this Project, switch to Plan for an implementation plan, or to Edit to get changes
          proposed for your review. Nothing is ever written until you approve a proposal.
        </div>
      )}
      {messages.map((m, i) => (
        <Message
          key={m.id}
          m={m}
          onRetry={i === messages.length - 1 ? onRetry : undefined}
          changeSets={changeSets.filter((c) => c.runId === m.runId)}
          onReview={onReview}
          commands={commands.filter((c) => c.runId === m.runId)}
          commandActions={commandActions}
        />
      ))}
      <div ref={end} />
    </div>
  );
}
